import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IdeaUpdateFormValues } from '@buyside/components/presentation/IdeaShowPage/AuthorActions/ideaUpdateForm';
import SelectField from '@buyside/components/presentation/Fields/selectField';
import DropzoneInput from '@buyside/components/presentation/Dropzone/DropzoneInput';

import { createAttachmentTypeOptions } from '@buyside/util/ideaUpdateFormHelper';
import { labelize } from '@buyside/util/arrayHelper';

import { ResearchAttachmentAttributes } from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/IdeaShowPage/AuthorActions/authorActionsStyles.scss';

const {
  ideaAttachmentTypes,
  ideaAttachmentFileTypes
} = require('buyside/util/constantsFromRails.json');

// When creating a new update, new attachments can be added, but editing the old attachments are not allowed (possible in idea edit).
// When editing an update, the existing attachments associated with the specific idea update (if any) can be edited. - TMA (30 Oct 2019)

const IdeaUpdateAttachment: FC<{
  attachment: ResearchAttachmentAttributes;
  idx: number;
  remove: (idx: number) => void;
}> = ({ attachment, idx, remove }) => {
  const researchTypeOptions = labelize(Object.keys(ideaAttachmentTypes), true);
  const attachmentTypeOptions = createAttachmentTypeOptions(attachment.researchType);

  const {
    setValue,
    formState: { errors: formErrors, isSubmitting }
  } = useFormContext<IdeaUpdateFormValues>();

  const errors = formErrors.attachments ? formErrors.attachments[idx] : null;

  const onDropFinish = ([file]: File[]): void => {
    if (file) {
      setValue(`attachments.${idx}.filename`, file.name, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue(`attachments.${idx}.file`, file, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  };

  return (
    <div className={styles.attachmentWrapper} key={`${attachment.id}-${idx}`}>
      <span className={styles.closeIcon} onClick={(): void => remove(idx)}>
        <FontAwesomeIcon icon={faTimesCircle} />
      </span>
      <DropzoneInput
        accept={ideaAttachmentFileTypes}
        disabled={isSubmitting}
        onDrop={onDropFinish}
        filename={attachment.filename}
        onRemove={(): void => remove(idx)}
      />
      {errors?.filename && (
        <>
          <div className={styles.errorText}>{errors.filename.message}</div>
          <br />
        </>
      )}
      <div className={styles.subGroup}>
        <SelectField
          label="Research Type"
          name={`attachments.${idx}.researchType`}
          onChangeCallback={() => setValue(`attachments.${idx}.attachmentType`, '')}
          options={researchTypeOptions}
        />
        <SelectField
          label="Attachment Type"
          name={`attachments.${idx}.attachmentType`}
          options={attachmentTypeOptions}
        />
      </div>
    </div>
  );
};

export default IdeaUpdateAttachment;
